import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { DownloadSimple, FileText } from "@phosphor-icons/react";

function fmt(n) { return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/invoices/${token}`)
      .then((r) => setInv(r.data))
      .catch(() => setErr("Invoice not found or link expired."));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">{err}</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Loading invoice…</div>;
  const c = inv.calculation || {};

  return (
    <div className="min-h-screen bg-slate-100 py-10" data-testid="public-invoice">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-start justify-between border-b border-slate-200 pb-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Tax Invoice</div>
            <div className="font-mono text-2xl font-bold text-slate-900 mt-1">{inv.invoice_number}</div>
            <div className="text-sm text-slate-500 mt-1">Date: {inv.date}</div>
          </div>
          <a href={`${API}/public/invoices/${token}/pdf`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
            <DownloadSimple size={16} weight="bold" /> Download PDF
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 py-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Billed To</div>
            <div className="mt-2 text-slate-900 font-medium">{inv.customer?.name}</div>
            {inv.customer?.address && <div className="text-sm text-slate-600 whitespace-pre-wrap">{inv.customer.address}</div>}
            {inv.customer?.phone && <div className="text-sm text-slate-600">Ph: {inv.customer.phone}</div>}
            {inv.customer?.gstin && <div className="text-sm text-slate-600">GSTIN: {inv.customer.gstin}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Amount Due</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">₹{fmt(c.total)}</div>
            <div className="text-xs text-slate-500 mt-1">{c.amount_in_words}</div>
          </div>
        </div>
        <table className="w-full text-sm border-t border-slate-200">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Qty</th>
              <th className="px-4 py-3 text-right">Rate</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(inv.items || []).map((it, i) => (
              <tr key={`${it.product_id || ""}-${it.description}-${i}`}>
                <td className="px-4 py-3">{it.description}</td>
                <td className="px-4 py-3 text-right">{it.qty}</td>
                <td className="px-4 py-3 text-right">₹{fmt(it.rate)}</td>
                <td className="px-4 py-3 text-right">₹{fmt(it.qty * it.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Sub Total</span><span>₹{fmt(c.sub_total)}</span></div>
          {c.discount_amount > 0 && (<div className="flex justify-between text-rose-600"><span>Discount</span><span>- ₹{fmt(c.discount_amount)}</span></div>)}
          <div className="flex justify-between text-slate-600"><span>Taxable</span><span>₹{fmt(c.taxable_value)}</span></div>
          <div className="flex justify-between text-slate-600"><span>SGST 2.5%</span><span>₹{fmt(c.sgst)}</span></div>
          <div className="flex justify-between text-slate-600"><span>CGST 2.5%</span><span>₹{fmt(c.cgst)}</span></div>
          {c.shipping > 0 && (<div className="flex justify-between text-slate-600"><span>Shipping</span><span>₹{fmt(c.shipping)}</span></div>)}
          <div className="flex justify-between border-t pt-2 mt-2 text-base font-bold text-slate-900"><span>Total</span><span>₹{fmt(c.total)}</span></div>
        </div>
      </div>
    </div>
  );
}
